import { useMemo, useState } from "react";
import styled, { useTheme } from "styled-components/macro";
import { IconButtonMedium } from "../Buttons/Buttons";
import { NewXIcon, SearchIcon } from "../Icons/Icons";
import { TextMdSemibold, TextSmRegular } from "../Typography/TypographyV2";
import { MenuItemAssignee } from "./MenuItemAssignee";

/** Figma IconButton close: no border (`border_none` + buttonIcon tokens). */
const CloseIconButton = styled(IconButtonMedium)`
  border: ${({ theme }) => theme.resolve("border_none")} solid
    ${({ theme }) => theme.resolve("color_buttonIcon_border_enabled")};
  background: ${({ theme }) => theme.resolve("color_buttonIcon_bg_enabled")};

  &:hover,
  &:focus,
  &:active {
    border: ${({ theme }) => theme.resolve("border_none")} solid
      ${({ theme }) => theme.resolve("color_buttonIcon_border_enabled")};
  }
`;

export type AssigneeOption = {
  id: string;
  name: string;
  email?: string | null;
};

export type AssigneePopOverProps = {
  /** Active approval stage name shown under the title (`{stageName} review`). */
  stageName: string;
  /** Approvers on the active stage only. */
  options: AssigneeOption[];
  /**
   * Currently assigned person id. When set, an Unassigned row is offered
   * first so the assignee can be cleared.
   */
  selectedId?: string | null;
  onSelect: (option: AssigneeOption | null) => void | Promise<void>;
  onClose: () => void;
  title?: string;
  unassignedLabel?: string;
  searchPlaceholder?: string;
  /** Appended after stage name in the subtitle. Default: `"review"`. */
  stageLabelSuffix?: string;
  className?: string;
  "data-testid"?: string;
};

const POPOVER_WIDTH_PX = 380;
const LIST_MAX_HEIGHT_PX = 320;

const Panel = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: ${POPOVER_WIDTH_PX}px;
  overflow: hidden;
  background-color: ${({ theme }) =>
    theme.resolve("color_bg_surface_standard")};
  border: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.resolve("color_border_lighter")};
  border-radius: ${({ theme }) => theme.resolve("radius_xs")};
  box-shadow: 0px 4px 16px 0px rgba(0, 0, 0, 0.12);
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.resolve("spacing_md")}
    ${({ theme }) => theme.resolve("spacing_lg")};
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.resolve("spacing_lg")};
  width: 100%;
`;

const HeaderCopy = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: ${({ theme }) => theme.resolve("spacing_xxs")};
  min-width: 0;
`;

const TitleStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.resolve("spacing_xs")};
`;

const SearchSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.resolve("spacing_md")};
  padding: ${({ theme }) => theme.resolve("spacing_none")}
    ${({ theme }) => theme.resolve("spacing_lg")}
    ${({ theme }) => theme.resolve("spacing_md")};
  border-bottom: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.resolve("color_border_lighter")};
`;

const SearchField = styled.label`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.resolve("spacing_md")};
  width: 100%;
  height: 40px;
  padding: ${({ theme }) => theme.resolve("spacing_md")}
    ${({ theme }) => theme.resolve("spacing_lg")};
  background-color: ${({ theme }) =>
    theme.resolve("color_bg_surface_standard")};
  border: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.resolve("color_border_input")};
  border-radius: ${({ theme }) => theme.resolve("radius_xs")};
  cursor: text;

  &:focus-within {
    border-color: ${({ theme }) => theme.resolve("color_border_input_active")};
  }
`;

const SearchInput = styled.input`
  flex: 1 1 auto;
  min-width: 0;
  margin: 0;
  padding: 0;
  border: none;
  outline: none;
  background: transparent;
  font-family: ${({ theme }) => theme.resolve("font_family_primary")};
  font-size: ${({ theme }) => theme.resolve("font_size_md")};
  font-weight: ${({ theme }) => theme.resolve("font_weight_regular")};
  line-height: ${({ theme }) => theme.resolve("line_height_md")};
  color: ${({ theme }) => theme.resolve("color_text_primary")};

  &::placeholder {
    color: ${({ theme }) => theme.resolve("color_text_tertiary")};
  }
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-height: ${LIST_MAX_HEIGHT_PX}px;
  overflow-y: auto;
  padding: ${({ theme }) => theme.resolve("spacing_xs")}
    ${({ theme }) => theme.resolve("spacing_none")};
`;

function matchesQuery(option: AssigneeOption, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) {
    return true;
  }
  return (
    option.name.toLowerCase().includes(q) ||
    (option.email?.toLowerCase().includes(q) ?? false)
  );
}

/**
 * Anchored assignee picker for an approval’s active stage.
 * Open from `AssigneeField`; pass stage approvers only (not a general user search).
 *
 * @see Agilis Design System — assigneePopOver
 */
export function AssigneePopOver({
  stageName,
  options,
  selectedId = null,
  onSelect,
  onClose,
  title = "Assignee",
  unassignedLabel = "Unassigned",
  searchPlaceholder = "Search",
  stageLabelSuffix = "review",
  className,
  "data-testid": dataTestId,
}: AssigneePopOverProps) {
  const theme = useTheme();
  const [query, setQuery] = useState("");

  const filteredOptions = useMemo(
    () =>
      options.filter(
        (option) => option.id !== selectedId && matchesQuery(option, query)
      ),
    [options, query, selectedId]
  );

  const showUnassigned =
    Boolean(selectedId) &&
    (!query.trim() ||
      unassignedLabel.toLowerCase().includes(query.trim().toLowerCase()));

  const subtitle = stageName.trim()
    ? `${stageName.trim()} ${stageLabelSuffix}`
    : stageLabelSuffix;

  return (
    <Panel
      className={className}
      data-testid={dataTestId}
      role="dialog"
      aria-label={title}
    >
      <Header>
        <HeaderRow>
          <HeaderCopy>
            <TitleStack>
              <TextMdSemibold color="primary">{title}</TextMdSemibold>
            </TitleStack>
            <TextSmRegular color="secondary">{subtitle}</TextSmRegular>
          </HeaderCopy>
          <CloseIconButton
            type="button"
            aria-label="Close"
            onClick={onClose}
            testid={
              dataTestId ? `${dataTestId}-close` : "assignee-popover-close"
            }
          >
            <NewXIcon />
          </CloseIconButton>
        </HeaderRow>
      </Header>

      <SearchSection>
        <SearchField>
          <SearchIcon
            width={20}
            height={20}
            fill={theme.resolve("color_icon_tertiary")}
          />
          <SearchInput
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={searchPlaceholder}
            aria-label={searchPlaceholder}
            autoFocus
          />
        </SearchField>
      </SearchSection>

      <List role="listbox" aria-label={title}>
        {showUnassigned && (
          <MenuItemAssignee
            type="unassigned"
            unassignedLabel={unassignedLabel}
            onClick={() => onSelect(null)}
            data-testid={
              dataTestId
                ? `${dataTestId}-unassigned`
                : "assignee-popover-unassigned"
            }
          />
        )}
        {filteredOptions.map((option) => (
          <MenuItemAssignee
            key={option.id}
            type="assigned"
            name={option.name}
            email={option.email}
            onClick={() => onSelect(option)}
            data-testid={
              dataTestId
                ? `${dataTestId}-option-${option.id}`
                : `assignee-popover-option-${option.id}`
            }
          />
        ))}
      </List>
    </Panel>
  );
}
